import { createSDKI18n, type SDKI18n } from '../../i18n'
import { XmaxSdkError } from '../../shared/sdk-error'
import { resolveUploadVideoSize } from './reference-video-size'
import type { VideoFileStream, VideoFileStreamOptions } from './video-file-stream'
import { createCanvasPosterUrl, drawSourceCover } from '../canvas/canvas-frame-draw'

import { resolveRtcPublishFps } from './publish-fps'

export type ImageFileStreamOptions = VideoFileStreamOptions

function loadImageElement(url: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    if (crossOrigin) {
      image.crossOrigin = 'anonymous'
    }
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('failed to load image'))
    image.src = url
  })
}

export async function createImageFileStream(
  fileOrUrl: Blob | string,
  options: ImageFileStreamOptions = {},
): Promise<VideoFileStream> {
  const i18n = options.i18n ?? createSDKI18n()
  const isUrl = typeof fileOrUrl === 'string'
  const url = isUrl ? fileOrUrl : URL.createObjectURL(fileOrUrl)

  let imageEl: HTMLImageElement
  try {
    imageEl = await loadImageElement(url, isUrl)
  } catch {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.imageLoadFailed'))
  }

  const sourceWidth = imageEl.naturalWidth || imageEl.width || 0
  const sourceHeight = imageEl.naturalHeight || imageEl.height || 0
  if (!sourceWidth || !sourceHeight) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.imageTrackMissing'))
  }

  const [outputWidth, outputHeight] = options.targetSize
    ?? resolveUploadVideoSize(sourceWidth, sourceHeight)

  const canvas = document.createElement('canvas')
  canvas.width = outputWidth
  canvas.height = outputHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('UNSUPPORTED_MEDIA', i18n.t('errors.captureStreamUnsupported'))
  }

  const fps = resolveRtcPublishFps({ mobile: options.mobile, fps: options.fps })
  const frameIntervalMs = Math.max(1, Math.round(1000 / fps))
  const captureStreamFn =
    (canvas as HTMLCanvasElement & { captureStream?: (frameRate?: number) => MediaStream }).captureStream ??
    (canvas as HTMLCanvasElement & { mozCaptureStream?: (frameRate?: number) => MediaStream }).mozCaptureStream

  if (!captureStreamFn) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('UNSUPPORTED_MEDIA', i18n.t('errors.captureStreamUnsupported'))
  }

  const previewStream = captureStreamFn.call(canvas, fps)
  const videoTrack = previewStream.getVideoTracks()[0]

  if (!videoTrack) {
    if (!isUrl) URL.revokeObjectURL(url)
    throw new XmaxSdkError('MEDIA_PROCESSING_ERROR', i18n.t('errors.imageTrackMissing'))
  }

  let intervalId: ReturnType<typeof setInterval> | null = null
  let destroyed = false

  const drawFrame = () => {
    if (destroyed) {
      return
    }
    drawSourceCover(
      ctx,
      imageEl,
      outputWidth,
      outputHeight,
      sourceWidth,
      sourceHeight,
      options.backgroundColor,
    )
  }

  drawFrame()
  const posterUrl = await createCanvasPosterUrl(canvas, options.poster)
  intervalId = setInterval(drawFrame, frameIntervalMs)

  const videoEl = document.createElement('video')
  videoEl.muted = true
  videoEl.playsInline = true

  const destroy = () => {
    destroyed = true

    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }

    try {
      videoTrack.stop()
    } catch {
    }

    try {
      previewStream.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      })
    } catch {
    }

    try {
      imageEl.src = ''
    } catch {
    }

    try {
      videoEl.remove()
    } catch {
    }

    if (posterUrl) {
      URL.revokeObjectURL(posterUrl)
    }
    if (!isUrl) URL.revokeObjectURL(url)
  }

  return {
    url,
    ...(posterUrl ? { posterUrl } : {}),
    previewStream,
    videoEl,
    videoTrack,
    fps,
    width: outputWidth,
    height: outputHeight,
    sourceWidth,
    sourceHeight,
    destroy,
  }
}
